import * as React from 'react';
import { useRouter } from '@mam/main/Router/useRouter';
import * as Wouter from '@mam/main/wouter';

const Link: React.FunctionComponent<{
	children: React.ReactElement<'a'>;
	newTarget?: boolean;
	subdomain?: string | null;
	to: string;
}> = ({ children, newTarget = false, subdomain, to }) => {
	const [, buildLink] = useRouter();
	const link = buildLink(to, subdomain);

	if (newTarget) {
		return React.cloneElement(children, {
			href: link.to,
			onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
				event.preventDefault();
				window.open(event.currentTarget.href, 'new');
			},
		});
	}
	return link.isRelative ? (
		<Wouter.Link to={to}>{children}</Wouter.Link>
	) : (
		React.cloneElement(children, { href: link.to })
	);
};

Link.displayName = 'Router.Link';

const Redirect: React.FunctionComponent<{
	data?: unknown;
	replace?: boolean;
	subdomain?: string | null;
	to: string;
}> = ({ data, replace, subdomain, to }) => {
	const [redirect] = useRouter();

	React.useLayoutEffect(() => {
		redirect(to, subdomain, replace, data);
	}, []);

	return null;
};

Redirect.displayName = 'Router.Redirect';

type ExpandParams<Params> = Params & { subdomain?: string | null };
type RedirectParams<Params> = ExpandParams<Params> & { data?: unknown; replace?: boolean };

// tslint:disable-next-line: no-any
export class Route<LinkParams = Record<never, never>, Target = React.FunctionComponent<any>> {
	public Link: React.FunctionComponent<
		ExpandParams<LinkParams & { children: React.ReactElement<'a'>; newTarget?: boolean }>
	>;
	public path: string;
	public Redirect: React.FunctionComponent<RedirectParams<LinkParams>>;
	public Target: Target;
	public useRedirect: () => (props: RedirectParams<LinkParams>) => void;

	public constructor(Target: Target, path: string, createLink: (params: LinkParams) => string) {
		this.Link = (props) => <Link to={createLink(props)} {...props} />;
		this.Link.displayName = 'Route.Link';

		this.path = path;

		this.Redirect = (props) => <Redirect to={createLink(props)} {...props} />;
		this.Redirect.displayName = 'Route.Redirect';

		this.Target = Target;
		this.useRedirect = () => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const [redirect] = useRouter();
			return (props) => {
				redirect(createLink(props), props.subdomain, props.replace, props.data);
			};
		};
	}
}
